/**
 * The SOP file format shanks stores in a memory row's body (mirrors
 * services/shanks/pkg/sops Parse/Encode): a front matter block with name,
 * title and when, then the markdown body.
 */ export const SOP_MAX_WHEN = 200;
export const SOP_MAX_TITLE = 120;
export const SOP_MAX_BODY = 15000;
const NAME_RE = /^[a-z0-9][a-z0-9-]{1,59}$/;
const oneLine = (s)=>s.split(/\s+/).filter(Boolean).join(' ');
export function encodeSop({ name, title, when, body }) {
    return `---\nname: ${name}\ntitle: ${oneLine(title)}\nwhen: ${oneLine(when)}\n---\n${body.trim()}\n`;
}
/** Reads the file format; fallbackName covers rows whose front matter has no name. */ export function parseSop(raw, fallbackName = '') {
    const out = {
        name: fallbackName,
        title: '',
        when: '',
        body: ''
    };
    let body = raw.replace(/^[﻿\r\n ]+/, '');
    if (body.startsWith('---\n')) {
        const rest = body.slice(4);
        const end = rest.indexOf('\n---');
        if (end !== -1) {
            for (const line of rest.slice(0, end).split('\n')){
                const colon = line.indexOf(':');
                if (colon === -1) continue;
                const key = line.slice(0, colon).trim().toLowerCase();
                const value = line.slice(colon + 1).trim();
                if (key === 'name' && value) out.name = value;
                else if (key === 'title') out.title = value;
                else if (key === 'when') out.when = value;
            }
            body = rest.slice(end + 4).replace(/^-/, '').replace(/^[\r\n]+/, '');
        }
    }
    out.body = body.trim();
    if (!out.title) out.title = out.name;
    return out;
}
/** The first problem with an SOP the user is about to save, or '' when it is valid. */ export function validateSop({ name, title, when, body }) {
    if (!NAME_RE.test(name)) return 'Name must be 2-60 characters of a-z, 0-9 and dashes.';
    if (!when.trim()) return 'When is required: one line on which requests should load this SOP.';
    if (when.length > SOP_MAX_WHEN) return `Keep "when" under ${SOP_MAX_WHEN} characters.`;
    if (title.length > SOP_MAX_TITLE) return `Keep the title under ${SOP_MAX_TITLE} characters.`;
    if (!body.trim()) return 'Body is required.';
    if (body.length > SOP_MAX_BODY) return `Keep the body under ${SOP_MAX_BODY} characters.`;
    return '';
}
/** Line diff (LCS) between two texts, for showing what a proposal changes. */ export function diffLines(before, after) {
    const a = before.split('\n');
    const b = after.split('\n');
    const n = a.length;
    const m = b.length;
    // lcs[i][j] = LCS length of a[i:] and b[j:]
    const lcs = Array.from({
        length: n + 1
    }, ()=>new Array(m + 1).fill(0));
    for(let i = n - 1; i >= 0; i--){
        for(let j = m - 1; j >= 0; j--){
            lcs[i][j] = a[i] === b[j] ? lcs[i + 1][j + 1] + 1 : Math.max(lcs[i + 1][j], lcs[i][j + 1]);
        }
    }
    const out = [];
    let i = 0;
    let j = 0;
    while(i < n && j < m){
        if (a[i] === b[j]) {
            out.push({
                kind: 'same',
                text: a[i]
            });
            i++;
            j++;
        } else if (lcs[i + 1][j] >= lcs[i][j + 1]) {
            out.push({
                kind: 'remove',
                text: a[i++]
            });
        } else {
            out.push({
                kind: 'add',
                text: b[j++]
            });
        }
    }
    while(i < n)out.push({
        kind: 'remove',
        text: a[i++]
    });
    while(j < m)out.push({
        kind: 'add',
        text: b[j++]
    });
    return out;
}
